import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Check, ListChecks, Minus, Plus } from "@phosphor-icons/react";

/**
 * PartialDialog — partial-delivery for ΠΑΡΑΓΓΕΛΙΑ post-its.
 *
 * Item model: { text, qty, delivered, done }
 *   - qty       = how many were originally ordered (>= 1)
 *   - delivered = how many were delivered so far (0..qty)
 *   - done      = derived: delivered >= qty
 * Backward-compat: older items have only {text, done} and are migrated to
 * qty=1, delivered=(done?1:0).
 */
export default function PartialDialog({ open, onOpenChange, note, onSave }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !note) return;
    const baseItems = Array.isArray(note?.data?.items) ? note.data.items : [];
    setItems(
      baseItems.map((it) => {
        const qty = Math.max(1, parseInt(it.qty ?? 1, 10) || 1);
        const delivered = Math.max(
          0,
          Math.min(
            qty,
            parseInt(it.delivered ?? (it.done ? qty : 0), 10) || 0
          )
        );
        return { text: it.text || "", qty, delivered, done: delivered >= qty };
      })
    );
  }, [open, note]);

  if (!note) return null;

  const customer = note?.data?.customer || "ΧΩΡΙΣ ΠΕΛΑΤΗ";
  const fullyDoneCount = items.filter((i) => i.done).length;
  const partialCount = items.filter(
    (i) => !i.done && i.delivered > 0
  ).length;
  const total = items.length;

  const setDelivered = (idx, value) =>
    setItems((prev) =>
      prev.map((x, i) => {
        if (i !== idx) return x;
        const delivered = Math.max(0, Math.min(x.qty, parseInt(value, 10) || 0));
        return { ...x, delivered, done: delivered >= x.qty };
      })
    );

  const bumpDelivered = (idx, delta) =>
    setItems((prev) =>
      prev.map((x, i) => {
        if (i !== idx) return x;
        const delivered = Math.max(0, Math.min(x.qty, x.delivered + delta));
        return { ...x, delivered, done: delivered >= x.qty };
      })
    );

  const toggleDone = (idx) =>
    setItems((prev) =>
      prev.map((x, i) => {
        if (i !== idx) return x;
        const willBeDone = !(x.delivered >= x.qty);
        return willBeDone
          ? { ...x, delivered: x.qty, done: true }
          : { ...x, delivered: 0, done: false };
      })
    );

  const markAllDone = () =>
    setItems((prev) =>
      prev.map((x) => ({ ...x, delivered: x.qty, done: true }))
    );

  const save = () => {
    onSave(note, items);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[560px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="partial-dialog"
      >
        <div
          className="px-7 pt-6 pb-3"
          style={{ backgroundColor: note.color || "#bae6fd" }}
        >
          <DialogHeader>
            <DialogTitle
              className="font-caveat text-3xl font-bold text-ink flex items-center gap-2"
              data-testid="partial-dialog-title"
            >
              <ListChecks size={22} weight="bold" />
              {customer}
            </DialogTitle>
            <DialogDescription className="text-ink/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΜΕΡΙΚΗ ΠΑΡΑΔΟΣΗ · {fullyDoneCount}/{total} ΟΛΟΚΛΗΡΩΜΕΝΑ
              {partialCount > 0 ? ` · ${partialCount} ΜΕΡΙΚΑ` : ""}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 pb-3 pt-4 bg-white space-y-2" data-testid="partial-items">
          {items.length === 0 ? (
            <p className="text-sm text-ink/55 italic text-center py-6">
              ΔΕΝ ΥΠΑΡΧΟΥΝ ΕΙΔΗ
            </p>
          ) : (
            items.map((it, idx) => {
              const remaining = Math.max(0, it.qty - it.delivered);
              const partial = !it.done && it.delivered > 0;
              return (
                <div
                  key={`${idx}-${it.text}`}
                  data-testid={`partial-item-${idx}`}
                  className={`rounded-xl border px-3 py-2.5 transition-all ${
                    it.done
                      ? "bg-green-50 border-green-200"
                      : partial
                      ? "bg-amber-50 border-amber-200"
                      : "bg-white border-black/10"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleDone(idx)}
                      data-testid={`partial-item-${idx}-toggle`}
                      title={it.done ? "ΑΚΥΡΩΣΗ ΟΛΟΚΛΗΡΩΣΗΣ" : "ΟΛΑ ΩΣ ΠΑΡΑΔΟΘΕΝΤΑ"}
                      className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-colors hover:scale-110 active:scale-95 ${
                        it.done
                          ? "bg-green-700 border-green-700 hover:bg-green-800"
                          : partial
                          ? "bg-amber-500 border-amber-500 hover:bg-amber-600"
                          : "bg-white border-ink/60 hover:border-ink"
                      }`}
                    >
                      {it.done && (
                        <Check size={14} weight="bold" className="text-white" />
                      )}
                      {partial && (
                        <span className="w-2 h-2 rounded-sm bg-white" />
                      )}
                    </button>
                    <span
                      className={`font-arial text-base leading-snug break-words flex-1 ${
                        it.done
                          ? "line-through text-ink/45"
                          : "text-ink/90 font-medium"
                      }`}
                    >
                      {it.text}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => bumpDelivered(idx, -1)}
                        disabled={it.delivered === 0}
                        data-testid={`partial-item-${idx}-minus`}
                        className="w-7 h-7 rounded-md text-ink/65 hover:bg-black/5 disabled:opacity-30 flex items-center justify-center"
                        title="-1"
                      >
                        <Minus size={12} weight="bold" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        max={it.qty}
                        value={it.delivered}
                        onChange={(e) => setDelivered(idx, e.target.value)}
                        data-testid={`partial-item-${idx}-delivered`}
                        className="w-12 text-center font-arial font-bold tabular-nums text-sm rounded-md border border-black/10 bg-white py-1"
                      />
                      <span className="text-ink/40 font-bold text-xs tabular-nums">
                        / {it.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => bumpDelivered(idx, 1)}
                        disabled={it.delivered >= it.qty}
                        data-testid={`partial-item-${idx}-plus`}
                        className="w-7 h-7 rounded-md text-ink/65 hover:bg-black/5 disabled:opacity-30 flex items-center justify-center"
                        title="+1"
                      >
                        <Plus size={12} weight="bold" />
                      </button>
                    </div>
                  </div>
                  {/* Status line */}
                  <div className="mt-1.5 ml-8 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider font-bold">
                    <span className="text-ink/55">ΑΡΧΙΚΑ {it.qty}</span>
                    {it.delivered > 0 && (
                      <span className="text-green-700">
                        · ΠΗΓΑΝ {it.delivered}
                      </span>
                    )}
                    {!it.done && remaining > 0 && (
                      <span className="text-amber-700">
                        · ΥΠΟΛΟΙΠΟ {remaining}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <div className="px-7 pb-3 bg-white">
            <button
              type="button"
              onClick={markAllDone}
              data-testid="partial-mark-all"
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink/55 hover:text-ink underline-offset-2 hover:underline"
            >
              ΣΗΜΕΙΩΣΗ ΟΛΩΝ ΩΣ ΟΛΟΚΛΗΡΩΜΕΝΑ
            </button>
          </div>
        )}

        <DialogFooter className="!justify-end gap-2 px-7 pb-5 bg-white">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-ink/70 hover:text-ink hover:bg-black/5 transition-colors"
            data-testid="partial-cancel"
          >
            ΑΚΥΡΟ
          </button>
          <button
            type="button"
            onClick={save}
            className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider bg-ink text-white hover:bg-ink/85 active:scale-95 transition-all"
            data-testid="partial-save"
          >
            ΑΠΟΘΗΚΕΥΣΗ
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
